import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, IconButton, ListItemText, Menu, MenuItem, Paper, Slider, Stack, Tooltip, Typography } from '@mui/material'
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded'
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded'
import { MAX_ZOOM, MIN_ZOOM, clampView, panView, presetView, viewName, zoomView } from './videoView'

const PRESETS = [
  { key: 'full', label: 'Full frame' },
  { key: 'left', label: 'Left half' },
  { key: 'right', label: 'Right half' }
]

// The transport bar's "View" button: the whole frame, one half of the ice, or a manual view.
export function VideoViewMenu ({ view, onChange, onAdjust, disabled = false }) {
  const [anchor, setAnchor] = useState(null)
  const current = viewName(view)
  const choose = action => {
    setAnchor(null)
    if (action === 'adjust') onAdjust()
    else onChange(presetView(action))
  }
  return <>
    <Button size="small" aria-haspopup="menu" aria-expanded={Boolean(anchor)} disabled={disabled}
      onClick={event => setAnchor(event.currentTarget)} title="Zoom the video to the part of the ice you want to watch">
      View: {current}
    </Button>
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
      {PRESETS.map(preset => <MenuItem key={preset.key} selected={current === preset.label} onClick={() => choose(preset.key)}>
        {preset.label}
      </MenuItem>)}
      <MenuItem selected={current === 'Custom'} onClick={() => choose('adjust')}>
        <ListItemText primary="Adjust manually…" secondary="Drag, scroll or pinch to zoom" />
      </MenuItem>
    </Menu>
  </>
}

VideoViewMenu.propTypes = {
  view: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdjust: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

const ZOOM_STEP = 1.25

// Covers the picture while adjusting: drag moves it, the wheel or a pinch zooms around the
// pointer, and the bar offers zoom buttons, a slider, Reset and Done. Playback controls come
// back on Done.
export function VideoViewAdjuster ({ view, onChange, onDone, saveNote }) {
  const surface = useRef(null)
  const pointers = useRef(new Map())
  const pinch = useRef(null)
  const latest = useRef(view)
  latest.current = view

  const fraction = event => {
    const box = surface.current.getBoundingClientRect()
    return { x: (event.clientX - box.left) / box.width, y: (event.clientY - box.top) / box.height, width: box.width, height: box.height }
  }

  const onPointerDown = event => {
    surface.current.setPointerCapture?.(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    pinch.current = null
  }

  const onPointerMove = event => {
    const previous = pointers.current.get(event.pointerId)
    if (!previous) return
    const box = surface.current.getBoundingClientRect()
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (pointers.current.size === 1) {
      onChange(panView(latest.current, (event.clientX - previous.x) / box.width, (event.clientY - previous.y) / box.height))
      return
    }
    const [a, b] = Array.from(pointers.current.values())
    const distance = Math.hypot(a.x - b.x, a.y - b.y)
    if (pinch.current && distance > 0) {
      const anchor = { x: ((a.x + b.x) / 2 - box.left) / box.width, y: ((a.y + b.y) / 2 - box.top) / box.height }
      onChange(zoomView(latest.current, distance / pinch.current, anchor))
    }
    pinch.current = distance
  }

  const onPointerUp = event => {
    pointers.current.delete(event.pointerId)
    pinch.current = null
  }

  const onWheel = event => {
    event.preventDefault()
    const { x, y } = fraction(event)
    onChange(zoomView(latest.current, Math.exp(-event.deltaY * 0.0015), { x, y }))
  }

  // React attaches wheel listeners as passive, so prevent page scrolling with a native listener,
  // attached before the first paint so a wheel turn right after opening already zooms.
  const wheel = useRef(null)
  wheel.current = onWheel
  React.useLayoutEffect(() => {
    const element = surface.current
    if (!element) return undefined
    // The bar's own scrolling (a native listener sees it despite React's stopPropagation) is not a zoom.
    const handler = event => { if (!event.target.closest?.('[data-view-toolbar]')) wheel.current(event) }
    element.addEventListener('wheel', handler, { passive: false })
    return () => element.removeEventListener('wheel', handler)
  }, [])

  return (
    <Box ref={surface} data-testid="video-view-surface" aria-label="Drag to move the video, scroll or pinch to zoom"
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      sx={{ position: 'absolute', inset: 0, zIndex: 3, cursor: 'grab', touchAction: 'none', outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2, '&:active': { cursor: 'grabbing' } }}>
      {/* The bar's own clicks and drags must not pan the picture. */}
      <Paper elevation={6} data-view-toolbar="" onPointerDown={event => event.stopPropagation()}
        sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', px: 1.5, py: 0.75, bgcolor: 'rgba(17,24,32,.92)', width: 'max-content', maxWidth: 'calc(100% - 16px)', cursor: 'default' }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Drag to move · scroll or pinch to zoom</Typography>
          <Tooltip title="Zoom out"><span><IconButton size="small" aria-label="Zoom out" disabled={view.zoom <= MIN_ZOOM}
            onClick={() => onChange(zoomView(view, 1 / ZOOM_STEP))}><ZoomOutRoundedIcon /></IconButton></span></Tooltip>
          <Slider size="small" aria-label="Zoom" min={MIN_ZOOM} max={MAX_ZOOM} step={0.05} value={view.zoom}
            onChange={(_event, value) => onChange(clampView({ ...view, zoom: value }))} sx={{ width: 110 }} />
          <Tooltip title="Zoom in"><span><IconButton size="small" aria-label="Zoom in" disabled={view.zoom >= MAX_ZOOM}
            onClick={() => onChange(zoomView(view, ZOOM_STEP))}><ZoomInRoundedIcon /></IconButton></span></Tooltip>
          <Button size="small" onClick={() => onChange(presetView('full'))}>Reset</Button>
          <Button size="small" variant="contained" onClick={onDone}>Done</Button>
        </Stack>
        {saveNote && <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25 }}>{saveNote}</Typography>}
      </Paper>
    </Box>
  )
}

VideoViewAdjuster.propTypes = {
  view: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  saveNote: PropTypes.string
}
